import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { Toaster, toast } from 'react-hot-toast';
import {
  FaArrowDown,
  FaArrowUp,
  FaBoxOpen,
  FaExclamationTriangle,
  FaSearch,
  FaTruck,
  FaUserShield,
  FaWallet,
} from 'react-icons/fa';
import { db } from '../../../config/firebaseConfig';
import { useUserProfile } from '../../../contexts/UserProfileContext';
import { SHIPMENT_STATUS, formatShipmentCost } from '../../../utils/shipments';
import {
  CARGO_DIRECTION,
  InsufficientStockError,
  getCargoStatus,
  isCargoOverdue,
  matchesCargoSearch,
  summarizeCompanyShipments,
} from '../../../utils/companyShipments';
import useCompanyShipments from './useCompanyShipments';
import CargoList from './CargoList';
import CargoFormModal from './CargoFormModal';
import CargoDetailDrawer from './CargoDetailDrawer';
import CargoAccessModal from './CargoAccessModal';
import styles from './CargoTracking.module.css';

// Özet kartları aynı zamanda hızlı filtredir
const QUICK_FILTERS = {
  all: () => true,
  incoming: (shipment) => shipment.direction === CARGO_DIRECTION.INCOMING,
  outgoing: (shipment) => shipment.direction !== CARGO_DIRECTION.INCOMING,
  inTransit: (shipment) => shipment.status === SHIPMENT_STATUS.SHIPPED,
  overdue: isCargoOverdue,
};

/**
 * Firma kargo takibi.
 *  - mode="company"  : Stok Yönetimi sekmesi — tüm kayıtlar + yetki yönetimi
 *  - mode="personal" : Profil sekmesi — yalnızca kullanıcının kendi kargoları
 */
const CargoTracking = ({ mode = 'company' }) => {
  const { employeeId, employeeData, userAuthorization } = useUserProfile();
  const isPersonal = mode === 'personal';
  const isAdmin =
    userAuthorization?.includes('admin') || userAuthorization?.includes('Kısıtlı Admin');
  const actorName = `${employeeData?.eName || ''} ${employeeData?.eSurname || ''}`.trim();

  const { shipments, loading, error, saveShipment, changeStatus, removeShipment } =
    useCompanyShipments({ ownerId: isPersonal ? employeeId : null, actorName });

  const [stocks, setStocks] = useState([]);
  const [quickFilter, setQuickFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [formState, setFormState] = useState(null); // { shipment, direction } | null
  const [showAccess, setShowAccess] = useState(false);
  const [busy, setBusy] = useState(false);

  // Stok kalemi seçimi için aktif stok kartları. Form her açıldığında okunur;
  // böylece "kalan" miktarlar kargo hareketlerinden sonra da günceldir.
  const formOpen = Boolean(formState);
  useEffect(() => {
    if (!formOpen) return undefined;
    let cancelled = false;
    getDocs(collection(db, 'Stocks'))
      .then((snapshot) => {
        if (cancelled) return;
        const options = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((stock) => stock['Durum'] !== 'inaktif')
          .map((stock) => ({
            id: stock.id,
            name: stock['Adı'] || 'İsimsiz stok',
            remaining: parseFloat(stock['Kalan']) || 0,
          }))
          .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
        setStocks(options);
      })
      .catch((stockError) => console.error('Stok listesi yüklenemedi:', stockError));
    return () => {
      cancelled = true;
    };
  }, [formOpen]);

  const summary = useMemo(() => summarizeCompanyShipments(shipments), [shipments]);
  const visible = useMemo(
    () =>
      shipments.filter(
        (shipment) => QUICK_FILTERS[quickFilter](shipment) && matchesCargoSearch(shipment, search)
      ),
    [shipments, quickFilter, search]
  );
  const selected = useMemo(
    () => shipments.find((shipment) => shipment.id === selectedId) || null,
    [shipments, selectedId]
  );

  const reportError = (actionError, fallback) => {
    console.error(fallback, actionError);
    toast.error(actionError instanceof InsufficientStockError ? actionError.message : fallback);
  };

  const handleSave = async (formData) => {
    try {
      await saveShipment(formState.shipment, formData, { id: employeeId, name: actorName });
      toast.success(formState.shipment ? 'Kargo kaydı güncellendi' : 'Kargo kaydı eklendi');
    } catch (saveError) {
      reportError(saveError, 'Kargo kaydı kaydedilemedi');
      throw saveError;
    }
  };

  const handleStatusChange = async (shipment, status) => {
    setBusy(true);
    try {
      await changeStatus(shipment, status);
      toast.success(`Durum "${getCargoStatus(shipment.direction, status).label}" olarak güncellendi`);
    } catch (statusError) {
      reportError(statusError, 'Durum güncellenemedi');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (shipment) => {
    const stockNote = (shipment.appliedItems || []).length > 0 ? ' Stoğa işlenen miktarlar geri alınır.' : '';
    if (!window.confirm(`"${shipment.subject}" kargo kaydı silinsin mi?${stockNote}`)) return;
    setBusy(true);
    try {
      await removeShipment(shipment);
      setSelectedId(null);
      toast.success('Kargo kaydı silindi');
    } catch (deleteError) {
      reportError(deleteError, 'Kargo kaydı silinemedi');
    } finally {
      setBusy(false);
    }
  };

  const closeForm = useCallback(() => setFormState(null), []);
  const closeDrawer = useCallback(() => setSelectedId(null), []);

  const stats = [
    { key: 'all', label: 'Toplam', value: summary.total, icon: FaBoxOpen, tone: 'neutral' },
    { key: 'incoming', label: 'Gelen', value: summary.incoming, icon: FaArrowDown, tone: 'success' },
    { key: 'outgoing', label: 'Giden', value: summary.outgoing, icon: FaArrowUp, tone: 'info' },
    { key: 'inTransit', label: 'Yolda', value: summary.inTransit, icon: FaTruck, tone: 'warning' },
    { key: 'overdue', label: 'Geciken', value: summary.overdue, icon: FaExclamationTriangle, tone: 'danger' },
  ];

  return (
    <div className={styles.cargoRoot}>
      {/* Profil sayfasının kendi Toaster'ı var; Stok Yönetimi react-toastify kullanıyor */}
      {!isPersonal && <Toaster position="top-right" />}
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>{isPersonal ? 'Kargolarım' : 'Kargo Takip'}</h2>
          <p className={styles.pageSubtitle}>
            {isPersonal
              ? 'Gönderdiğiniz ve teslim aldığınız firma kargoları'
              : 'Firmaya gelen ve firmadan giden tüm kargolar'}
          </p>
        </div>
        <div className={styles.pageActions}>
          {!isPersonal && isAdmin && (
            <button type="button" className={styles.outlineButton} onClick={() => setShowAccess(true)}>
              <FaUserShield /> Yetkiler
            </button>
          )}
          <button
            type="button"
            className={styles.outlineButton}
            onClick={() => setFormState({ shipment: null, direction: CARGO_DIRECTION.INCOMING })}
          >
            <FaArrowDown /> Kargo Al
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => setFormState({ shipment: null, direction: CARGO_DIRECTION.OUTGOING })}
          >
            <FaArrowUp /> Kargo Gönder
          </button>
        </div>
      </div>

      <div className={styles.stats}>
        {stats.map(({ key, label, value, icon: Icon, tone }) => (
          <button
            key={key}
            type="button"
            className={`${styles.stat} ${styles[`stat_${tone}`]} ${quickFilter === key ? styles.statActive : ''}`}
            onClick={() => setQuickFilter(key)}
            aria-pressed={quickFilter === key}
          >
            <span className={styles.statIcon}><Icon /></span>
            <span className={styles.statValue}>{value}</span>
            <span className={styles.statLabel}>{label}</span>
          </button>
        ))}
        <div className={`${styles.stat} ${styles.statStatic}`}>
          <span className={styles.statIcon}><FaWallet /></span>
          <span className={styles.statValue}>{formatShipmentCost(summary.totalCost)}</span>
          <span className={styles.statLabel}>Kargo Bedeli</span>
        </div>
      </div>

      <label className={styles.searchBox}>
        <FaSearch />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Konu, kişi, kurum veya takip numarası ara..."
          aria-label="Kargo ara"
        />
      </label>

      {loading ? (
        <div className={styles.skeletonList} aria-busy="true" aria-label="Yükleniyor">
          <span /><span /><span />
        </div>
      ) : error ? (
        <div className={styles.errorState} role="alert">{error}</div>
      ) : visible.length === 0 ? (
        <div className={styles.blankState}>
          <FaBoxOpen />
          <strong>{shipments.length === 0 ? 'Henüz kargo kaydı yok' : 'Eşleşen kargo bulunamadı'}</strong>
          <p>
            {shipments.length === 0
              ? '"Kargo Gönder" veya "Kargo Al" ile ilk kaydı oluşturun.'
              : 'Arama ifadesini veya filtreyi değiştirmeyi deneyin.'}
          </p>
        </div>
      ) : (
        <CargoList shipments={visible} showOwner={!isPersonal} onSelect={(shipment) => setSelectedId(shipment.id)} />
      )}

      <CargoDetailDrawer
        shipment={formState ? null : selected}
        canEdit
        busy={busy}
        onClose={closeDrawer}
        onEdit={(shipment) => setFormState({ shipment, direction: shipment.direction })}
        onDelete={handleDelete}
        onStatusChange={handleStatusChange}
      />
      <CargoFormModal
        show={Boolean(formState)}
        onHide={closeForm}
        shipment={formState?.shipment || null}
        initialDirection={formState?.direction}
        stocks={stocks}
        onSave={handleSave}
      />
      <CargoAccessModal show={showAccess} onHide={() => setShowAccess(false)} />
    </div>
  );
};

export default CargoTracking;
